import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaTimes } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { createPromoCode, clearError, clearSuccess } from '../../../redux/Slices/AdminPromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';


const AdminAddPromoCode = () => {
  
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error, success } = useSelector(state => state.adminPromoCodes);

  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    min_order: '',
    max_order: '',
    usage_limit: '',
    starts_at: '',
    expires_at: '',
    status: 1
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setTitle('إضافة برومو كود');
    dispatch(clearError());
    dispatch(clearSuccess());
  }, [setTitle, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      navigate('/admin-promo-codes');
    }
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // منع إدخال القيم السالبة للخصم والحد الأدنى للطلب
    if (name === 'discount_percent' || name === 'min_order' || name === 'max_order') {
      const numValue = parseFloat(value);
      if (value !== '' && (isNaN(numValue) || numValue < 0)) {
        return; // لا تحديث القيمة إذا كانت سالبة
      }
    }
    
    // منع الكسور في نسبة الخصم - فقط أرقام صحيحة
    if (name === 'discount_percent' && value.includes('.')) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'كود البرومو مطلوب';
    } else if (formData.code.length < 3) {
      newErrors.code = 'كود البرومو يجب أن يكون 3 أحرف على الأقل';
    }

    if (!formData.discount_percent) {
      newErrors.discount_percent = 'نسبة الخصم مطلوبة';
    } else if (formData.discount_percent <= 0) {
      newErrors.discount_percent = 'نسبة الخصم لا يمكن أن تكون صفر أو سالبة';
    } else if (formData.discount_percent < 1 || formData.discount_percent > 100) {
      newErrors.discount_percent = 'نسبة الخصم يجب أن تكون بين 1% و 100%';
    }

    if (!formData.min_order) {
      newErrors.min_order = 'الحد الأدنى للطلب مطلوب';
    } else if (formData.min_order < 0) {
      newErrors.min_order = 'الحد الأدنى للطلب لا يمكن أن يكون سالب';
    }

    if (formData.max_order && parseFloat(formData.max_order) < 0) {
      newErrors.max_order = 'الحد الأقصى للطلب لا يمكن أن يكون سالب';
    } else if (formData.max_order && formData.min_order && parseFloat(formData.max_order) < parseFloat(formData.min_order)) {
      newErrors.max_order = 'الحد الأقصى يجب أن يكون أكبر من أو يساوي الحد الأدنى';
    }

    if (formData.usage_limit && formData.usage_limit < 1) {
      newErrors.usage_limit = 'حد الاستخدام يجب أن يكون أكبر من 0';
    }

    if (!formData.starts_at) {
      newErrors.starts_at = 'تاريخ البداية مطلوب';
    }

    if (!formData.expires_at) {
      newErrors.expires_at = 'تاريخ الانتهاء مطلوب';
    } else if (formData.starts_at && formData.expires_at && new Date(formData.expires_at) <= new Date(formData.starts_at)) {
      newErrors.expires_at = 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البداية';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      discount_percent: parseInt(formData.discount_percent),
      min_order: parseFloat(formData.min_order),
      max_order: formData.max_order ? parseFloat(formData.max_order) : null,
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
    };

    dispatch(createPromoCode(submitData));
  };

  const handleCancel = () => {
    navigate('/admin-promo-codes');
  };

  return (
    <div className="main_content">
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box">
              <div className="page-title-right mb-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleCancel}
                >
                  <FaArrowLeft className="mx-2" />
                  العودة
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <motion.div
              className="card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        كود البرومو <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        placeholder="أدخل كود البرومو"
                        dir="ltr"
                      />
                      {errors.code && (
                        <div className="invalid-feedback">
                          {errors.code}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        نسبة الخصم (%) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.discount_percent ? 'is-invalid' : ''}`}
                        name="discount_percent"
                        value={formData.discount_percent}
                        onChange={handleInputChange}
                        placeholder="أدخل نسبة الخصم"
                        min="1"
                        max="100"
                        step="1"
                        onKeyDown={(e) => {
                          // منع إدخال الرموز السالبة
                          if (e.key === '-' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {errors.discount_percent && (
                        <div className="invalid-feedback">
                          {errors.discount_percent}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        الحد الأدنى للطلب (جنيه) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.min_order ? 'is-invalid' : ''}`}
                        name="min_order"
                        value={formData.min_order}
                        onChange={handleInputChange}
                        placeholder="أدخل الحد الأدنى للطلب"
                        min="0"
                        step="0.01"
                        onKeyDown={(e) => {
                          // منع إدخال الرموز السالبة
                          if (e.key === '-') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {errors.min_order && (
                        <div className="invalid-feedback">
                          {errors.min_order}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        الحد الأقصى للطلب (جنيه)
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.max_order ? 'is-invalid' : ''}`}
                        name="max_order"
                        value={formData.max_order}
                        onChange={handleInputChange}
                        placeholder="أدخل الحد الأقصى للطلب"
                        min="0"
                        step="0.01"
                        onKeyDown={(e) => {
                          // منع إدخال الرموز السالبة
                          if (e.key === '-') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {errors.max_order && (
                        <div className="invalid-feedback">
                          {errors.max_order}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        حد الاستخدام
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.usage_limit ? 'is-invalid' : ''}`}
                        name="usage_limit"
                        value={formData.usage_limit}
                        onChange={handleInputChange}
                        placeholder="اترك فارغ للاستخدام غير المحدود"
                        min="1"
                      />
                      {errors.usage_limit && (
                        <div className="invalid-feedback">
                          {errors.usage_limit}
                        </div>
                      )}
                      <div className="form-text">
                        اترك فارغ للسماح بالاستخدام غير المحدود
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        تاريخ البداية <span className="text-danger">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className={`form-control ${errors.starts_at ? 'is-invalid' : ''}`}
                        name="starts_at"
                        value={formData.starts_at}
                        onChange={handleInputChange}
                      />
                      {errors.starts_at && (
                        <div className="invalid-feedback">
                          {errors.starts_at}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        تاريخ الانتهاء <span className="text-danger">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className={`form-control ${errors.expires_at ? 'is-invalid' : ''}`}
                        name="expires_at"
                        value={formData.expires_at}
                        onChange={handleInputChange}
                      />
                      {errors.expires_at && (
                        <div className="invalid-feedback">
                          {errors.expires_at}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        الحالة <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value={1}>نشط</option>
                        <option value={0}>غير نشط</option>
                      </select>
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          <FaTimes className="mx-2" />
                          إلغاء
                        </button>
                        <button
                          type="submit"
                          className="btn"
                          disabled={loading}
                          style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                              جاري الحفظ...
                            </>
                          ) : (
                            <>
                              <FaSave className="mx-2" />
                              حفظ
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAddPromoCode;
